import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PurchaseShell } from "../features/purchase/PurchaseShell";
import { Toolbar } from "../components/data/Toolbar";
import { FilterRow } from "../components/data/FilterRow";
import { DataTable } from "../components/data/DataTable";
import { supplierColumns } from "../features/purchase/supplierColumns";
import { archiveSupplier, listSuppliers } from "../api/purchases";
import { useCachedResource } from "../hooks/useCachedResource";

const CURRENCIES = ["IQD", "AED", "USD", "SAR", "EUR", "JOD"];

export default function PurchaseSuppliersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [currency, setCurrency] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setQ(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  const { data, reload: load } = useCachedResource(
    `purch-suppliers-${q}`,
    () => listSuppliers({ search: q || undefined, limit: 200 }).then((d) => d.items || []).catch(() => []),
    [q],
  );
  const items = data || [];

  const rows = useMemo(
    () => items.filter((row) =>
      (!currency || row.currency === currency)
      && (!status || (status === "archived") === Boolean(row.is_archived))),
    [items, currency, status],
  );

  const columns = useMemo(
    () => supplierColumns({
      t, i18n,
      onOpen: (row) => navigate(`/purchases/suppliers/${row.id}`),
      onArchive: async (row) => {
        await archiveSupplier(row.id);
        toast.success(t("app.delete"));
        await load();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n, navigate],
  );

  return (
    <PurchaseShell titleKey="suppliers">
      <Toolbar
        testid="supplier-toolbar"
        search={search}
        onSearch={setSearch}
        placeholder={t("purchases.searchSupplier")}
        actionLabel={t("purchases.addSupplier")}
        onAction={() => navigate("/purchases/suppliers/new")}
      />
      <FilterRow
        testid="supplier-filters"
        filters={[
          { key: "status", value: status, onChange: setStatus,
            allLabel: t("purchases.filters.allStates"),
            options: ["active", "archived"].map((key) => ({ value: key,
              label: t(`purchases.supplierStates.${key}`) })) },
          { key: "currency", value: currency, onChange: setCurrency,
            allLabel: t("purchases.filters.allCurrencies"),
            options: CURRENCIES.map((code) => ({ value: code, label: code })) },
        ]}
      />
      <DataTable
        testid="supplier-table"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/purchases/suppliers/${row.id}`)}
        empty={search ? t("purchases.noSearchResults") : t("purchases.noSuppliers")}
      />
    </PurchaseShell>
  );
}
